import { Divider, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Card } from './components/UserCard';
import { useEffect, useRef, useState } from 'react';
import { fetchTicketById, replyTicket } from '@/store/Ticket';
import { useDispatch } from 'react-redux';
import RemoteFile from '@/helper/RemoteFile';
import { MessageInput } from './components/MessageInput';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

type Replay = {
  id: string;
  ticket_id: string;
  user_id: string;
  replay: string;
  created_at: string;
  user_name: string;
  user_pic: string;
  photo_url: string | null;
};

type Ticket = {
  id: string;
  tag_id: string;
  user_id: string;
  description: string;
  status: string;
  pic_url: string;
  resolved_at: string;
  is_deleted: string;
  created_at: string;
  user_name: string;
  user_pic: string;
  issue_tag_name: string;
};

type MainT = {
  replay: Replay[];
  ticket: Ticket;
};

export const ShowTicket: React.FC = () => {
  const { id } = useParams();
  const [ticket, setTicket] = useState<MainT>();
  const dispatch = useDispatch<any>();
  const [replaySend, setReplaySend] = useState();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const data = async () => {
      const res = await dispatch(fetchTicketById(id as string));
      setTicket(res.payload.data);
    };
    data();
  }, [replaySend, id]);

  useEffect(() => {
    if (ticket && ticket?.replay?.length >= 0) {
      scrollToBottom();
    }
  }, [ticket?.replay?.length]);

  const sendReplay = async (text: string) => {
    if (!text || !id) return;
    const formData = new FormData();
    if (imageFile) {
      formData.append('imageFile', imageFile);
    }
    formData.append('replay', text);

    const res = await dispatch(replyTicket({ ticketId: id, formData }));
    setReplaySend(res.payload.data);
    setImageFile(null);
  };

  return (
    <div className='p-8 rounded-[24px] bg-white flex flex-col h-full min-h-0 '>
      <div>
        <div className='flex flex-row justify-between'>
          <Typography
            sx={{
              backgroundColor: '#FF0050',
              borderRadius: '50px',
              color: 'white',
              p: '5px 15px',
            }}
          >
            Ticket ID: {id}
          </Typography>
          {ticket?.ticket.status === 'resolved' && (
            <Typography
              sx={{
                backgroundColor: 'none',
                borderRadius: '50px',
                color: 'black',
                p: '5px 15px',
              }}
            >
              <CheckCircleOutlineIcon sx={{ mb: 0.1 }} /> Mark as Solved
            </Typography>
          )}
        </div>
        <div>
          <Typography sx={{ fontSize: '40px', fontWeight: 'bold', mt: 2 }}>
            {ticket?.ticket.description}
          </Typography>
        </div>
        <div>
          <Typography sx={{ mb: 1.5 }}>
            {ticket?.ticket.issue_tag_name}
          </Typography>
        </div>
        <Divider />
      </div>
      <div ref={scrollRef} className='flex-1 overflow-auto'>
        <div className='flex flex-col bg-[#F5F5F5] rounded-lg p-8 max-w-[700px] mt-5'>
          <Card
            pic={ticket?.ticket.user_pic}
            name={ticket?.ticket.user_name}
            time={ticket?.ticket.created_at}
          />
          <Typography sx={{ fontSize: '18px', mt: 2, mb: 2, color: '#4D4D4D' }}>
            {ticket?.ticket.description}
          </Typography>
          <RemoteFile
            keyPath={ticket?.ticket.pic_url || 'no pic'}
            key={ticket?.ticket.pic_url}
            className='h-[130px] w-[87px] object-cover rounded'
          />
        </div>
        {ticket?.replay.map((r) => (
          <div className='flex flex-col bg-[#F5F5F5] rounded-lg p-8 max-w-[700px] mt-2'>
            <Card pic={r.user_pic} name={r.user_name} time={r.created_at} />
            <Typography sx={{ fontSize: '18px', mt: 2, color: '#4D4D4D' }}>
              {r.replay}
            </Typography>
            {r.photo_url && (
              <RemoteFile
                keyPath={r.photo_url || 'no pic'}
                key={r.photo_url}
                className='max-h-[230px] max-w-[500px] object-contain rounded self-start mt-3'
              />
            )}
          </div>
        ))}
      </div>
      {ticket?.ticket.status !== 'resolved' && (
        <div className='pt-4'>
          {imageFile && (
            <img
              src={URL.createObjectURL(imageFile)}
              alt='Preview'
              style={{ maxWidth: '500px', maxHeight: '250px' }}
            />
          )}

          <MessageInput
            onSend={(txt) => sendReplay(txt)}
            onAttachFiles={(files) => setImageFile(files[0])}
          />
        </div>
      )}
    </div>
  );
};
